import type { Locale } from '@/i18n/locales'

import {
    CANONICAL_CURRENCY,
    isDisplayCurrency,
    LOCALE_DEFAULT_CURRENCY,
    type DisplayCurrency,
} from './map'

/** The query parameter carrying an explicit display currency. Shared with the other
 *  Wanderu surfaces — renaming it breaks currency continuity across a link. */
export const CURRENCY_PARAM = 'cur'

/**
 * The display currency a URL's query **selects** — the value a link carries onward and the
 * picker reports — or `undefined` for none.
 *
 * `undefined` covers every shape that selects nothing: no `cur` at all, a repeated one, a
 * malformed one, a currency this app does not display, and one that merely restates the
 * locale's own default. The routing layer redirects those last three away before a page is
 * served, but the query is untrusted whatever reached it, and a reader of it must not be
 * the one place that trusts it (`conventions.md#validate-untrusted-input-at-boundary`).
 *
 * The judgment is `resolveDisplayCurrency`'s, so the currency a page's prices are stated in
 * and the currency its links carry cannot come from two different readings of one URL.
 *
 * Takes the parameters rather than a URL so each reader can hand over what it holds: a
 * `URLSearchParams` on the server, the router's read-only copy on the client. `null` —
 * which the client hook answers wherever no route context has resolved — is no selection.
 */
export function selectedDisplayCurrency(
    locale: Locale,
    params: Pick<URLSearchParams, 'getAll'> | null,
): DisplayCurrency | undefined {
    const values = params?.getAll(CURRENCY_PARAM) ?? []
    if (values.length === 0) {
        return undefined
    }

    // A repeated parameter states no single currency: handed over as an array, it lands on
    // the redirect verdict and so selects nothing.
    const { currency, redirect } = resolveDisplayCurrency(
        locale,
        values.length === 1 ? values[0] : values,
    )
    return redirect || currency === LOCALE_DEFAULT_CURRENCY[locale] ? undefined : currency
}

export interface CurrencyResolution {
    /** The currency the page states its prices in. */
    currency: DisplayCurrency
    /**
     * True when the URL's currency value must be stripped and the request redirected to
     * the param-free form: the value is redundant (equal to the
     * locale default) or unusable (malformed, or a currency this app does not display).
     * Serving the page anyway would answer a URL whose stated currency was ignored.
     */
    redirect: boolean
}

/** ISO 4217 alphabetic codes are exactly three letters. Anything else never named a
 *  currency, so it is rejected before the displayable check rather than after. */
const ISO_4217_ALPHA = /^[A-Za-z]{3}$/

/**
 * Decide the display currency for a request, from its locale and any explicit currency
 * its URL carries — and nothing else. No header, cookie, session or visitor
 * attribute participates, so the same URL yields the same currency for every visitor and
 * a shared cache can serve one response to all of them.
 *
 * Total over the values it may receive: every input yields a declared currency and
 * none throws. `cur` arrives straight off a URL, so it is untrusted — an array (a repeated
 * param), a non-string, or a malformed value all resolve rather than reaching a formatter.
 */
export function resolveDisplayCurrency(locale: Locale, cur: unknown): CurrencyResolution {
    const localeDefault = LOCALE_DEFAULT_CURRENCY[locale]

    if (cur === undefined || cur === null) {
        return { currency: localeDefault, redirect: false }
    }

    // A repeated `?cur=` reaches a reader as an array: the URL states no single currency,
    // so there is nothing to honour and picking one would invent an answer.
    if (typeof cur !== 'string' || !ISO_4217_ALPHA.test(cur.trim())) {
        return { currency: localeDefault, redirect: true }
    }

    const requested = cur.trim().toUpperCase()
    if (!isDisplayCurrency(requested) || requested === localeDefault) {
        return { currency: localeDefault, redirect: true }
    }

    return { currency: requested, redirect: false }
}

/**
 * The currency a page's prices were actually stated in — which is the selected display
 * currency only when a rate was available to convert at.
 *
 * This is what the page's other layers read so a published price and its currency code
 * describe the amount the visitor sees: on the fallback path the amounts are
 * canonical, so the offer markup must say `USD` even though the URL selected `MXN`.
 * Deriving it from the rate rather than tracking a second flag is deliberate — the rate's
 * absence IS the fallback, so the two cannot drift apart.
 */
export function renderedCurrency(
    displayCurrency: DisplayCurrency,
    rate: number | undefined,
): DisplayCurrency {
    return rate === undefined ? CANONICAL_CURRENCY : displayCurrency
}
